'use strict';

import { UserDocumentCollection } from '../../Collections/User/UserDocumentCollection';
import { UserLetterCollection } from '../../Collections/User/UserLetterCollection';
import { UserSunfireCollection } from '../../Collections/User/UserSunfireCollection';
import { UserConnectureCollection } from '../../Collections/User/UserConnectureCollection';
import { UserClientPortalCollection } from '../../Collections/User/UserClientPortalCollection';
import { UserOfficeCollection } from '../../Collections/User/UserOfficeCollection';
import { UserPersonalTokenCollection } from '../../Collections/User/UserPersonalTokenCollection';
import { UserNotificationPreferenceCollection } from '../../Collections/User/UserNotificationPreferenceCollection';
import { IpListCollection } from '../../Collections/IpListCollection';
import { Entity } from '../Entity';
import { UserCompanyLogo } from './UserCompanyLogo';
import { UserCalendarProvider } from './UserCalendarProvider';
import { UserDocument } from './UserDocument';
import { UserLetter } from './UserLetter';
import { UserSignature } from './UserSignature';
import { UserSignatureEmail } from './UserSignatureEmail';
import { UserSignaturePic } from './UserSignaturePic';
import { UserSunfire } from './UserSunfire';
import { UserConnecture } from './UserConnecture';
import { UserClientPortal } from './UserClientPortal';
import { UserOffice } from './UserOffice';
import { UserNotificationPreference } from './UserNotificationPreference';
import { UserPersonalToken } from './UserPersonalToken';
import { AgencyAgentNpn } from '../Agency/AgencyAgentNpn';
import { UserProfilePic } from './UserProfilePic';
import { IpList } from '../IpList';

type UserType = 'agencyAgent' | 'single';
type UserSubType = 'contractor' | 'loa';
type UserSubTypeDesignation = 'community' | 'fronter';

export type UserScope = {
  userType: UserType;
  userSubType: UserSubType;
  userSubTypeDesignation?: UserSubTypeDesignation;
  ownerId?: number;
  loa?: number;
  agencyId?: number;
  agencyMember?: boolean;
  hasLoaAgents?: boolean;
  hasLoaCommunityAgents?: boolean;
};

export class User extends Entity {
  protected userId;
  protected userStatus;
  protected userTrialStart;
  protected userUsername;
  protected userPassword;
  protected userCompanyName;
  protected userFirstname;
  protected userLastname;
  protected userAddressStreet;
  protected userAddressUnit;
  protected userAddressCity;
  protected userAddressState;
  protected userAddressZip;
  protected userPhone;
  protected userEmailConfirmCode;
  protected userTempPassword;
  protected userHearabout;
  protected userFmo;
  protected userCurrentDb;
  protected userAgree;
  protected userFirstLogin;
  protected userTimezone;
  protected userPhoneProvider;
  protected userLastLogin;
  protected needsMigration;
  protected userTitle;
  protected userCnxAccess;
  protected userMfa;
  protected userMfaNumber;
  protected showCalendarOnDashboard: boolean;
  protected documents;
  protected letters;
  protected clientPortals;
  protected offices;
  protected notificationPreferences;
  protected signature;
  protected signatureEmail;
  protected signaturePic;
  protected companyLogo;
  protected calendar;
  protected userSunfire;
  protected userConnecture;
  protected userPersonalTokens;
  protected userNpn;
  protected userProfilePic: UserProfilePic;
  protected ips;

  constructor() {
    super();
  }

  public hydrate(data: any) {
    this.userId = data.userId;
    this.userStatus = data.userStatus;
    this.userTrialStart = data.userTrialStart;
    this.userUsername = data.userUsername;
    this.userCompanyName = data.userCompanyName;
    this.userFirstname = data.userFirstname;
    this.userLastname = data.userLastname;
    this.userAddressStreet = data.userAddressStreet;
    this.userAddressUnit = data.userAddressUnit;
    this.userAddressCity = data.userAddressCity;
    this.userAddressState = data.userAddressState;
    this.userAddressZip = data.userAddressZip;
    this.userPhone = data.userPhone;
    this.userHearabout = data.userHearabout;
    this.userFmo = data.userFmo;
    this.userCurrentDb = data.userCurrentDb;
    this.userAgree = data.userAgree;
    this.userTimezone = data.userTimezone;
    this.userPhoneProvider = data.userPhoneProvider;
    this.userLastLogin = data.userLastLogin;
    this.userTitle = data.userTitle;
    this.showCalendarOnDashboard = data.showCalendarOnDashboard;
    this.userMfa = data.userMfa;
    this.userMfaNumber = data.userMfaNumber;

    if (data.npn) {
      const npn = new AgencyAgentNpn();
      npn.hydrate(data.npn);

      this.userNpn = npn;
    }

    if (data.profilePic) {
      const profilePic = new UserProfilePic();
      profilePic.hydrate(data.profilePic);

      this.userProfilePic = profilePic;
    }

    if (data.documents) {
      const documents = new UserDocumentCollection();

      data.documents.forEach((singleDocumentData) => {
        const userDocument = new UserDocument();
        userDocument.hydrate(singleDocumentData);

        documents.addItem(userDocument);
      });

      this.documents = documents;
    }

    if (data.ips) {
      const ips = new IpListCollection();

      data.ips.forEach((singleIpData) => {
        const userIp = new IpList();
        userIp.hydrate(singleIpData);

        ips.addItem(userIp);
      });

      this.ips = ips;
    }

    if (data.userSunfire) {
      const sunfire = new UserSunfireCollection();

      data.userSunfire.forEach((singleSunfireData) => {
        const userSunfire = new UserSunfire();
        userSunfire.hydrate(singleSunfireData);

        sunfire.addItem(userSunfire);
      });

      this.userSunfire = sunfire;
    }

    if (data.userConnecture) {
      const connecture = new UserConnectureCollection();

      data.userConnecture.forEach((singleConnectureData) => {
        const userConnecture = new UserConnecture();
        userConnecture.hydrate(singleConnectureData);

        connecture.addItem(userConnecture);
      });

      this.userConnecture = connecture;
    }

    if (data.personalTokens) {
      const personalToken = new UserPersonalTokenCollection();

      data.personalTokens.forEach((singlePersonalTokenData) => {
        const personalTokens = new UserPersonalToken();
        personalTokens.hydrate(singlePersonalTokenData);

        personalToken.addItem(personalTokens);
      });

      this.userPersonalTokens = personalToken;
    }

    if (data.letters) {
      const letters = new UserLetterCollection();

      data.letters.forEach((singleLetterData) => {
        const userLetter = new UserLetter();
        userLetter.hydrate(singleLetterData);

        letters.addItem(userLetter);
      });

      this.letters = letters;
    }

    if (data.clientPortals) {
      const clientPortals = new UserClientPortalCollection();

      data.clientPortals.forEach((singleClientPortalData) => {
        const userClientPortal = new UserClientPortal();
        userClientPortal.hydrate(singleClientPortalData);

        clientPortals.addItem(userClientPortal);
      });

      this.clientPortals = clientPortals;
    }

    if (data.offices) {
      const offices = new UserOfficeCollection();

      data.offices.forEach((singleOfficeData) => {
        const userOffice = new UserOffice();
        userOffice.hydrate(singleOfficeData);

        offices.addItem(userOffice);
      });

      this.offices = offices;
    }

    if (data.notificationPreferences) {
      const notificationPreferences = new UserNotificationPreferenceCollection();

      data.notificationPreferences.forEach((singleNotificationPreferenceData) => {
        const userNotificationPreference = new UserNotificationPreference();
        userNotificationPreference.hydrate(singleNotificationPreferenceData);

        notificationPreferences.addItem(userNotificationPreference);
      });

      this.notificationPreferences = notificationPreferences;
    }

    if (data.signature) {
      const signature = new UserSignature();
      signature.hydrate(data.signature);

      this.signature = signature;
    }

    if (data.signatureEmail) {
      const signatureEmail = new UserSignatureEmail();
      signatureEmail.hydrate(data.signatureEmail);

      this.signatureEmail = signatureEmail;
    }

    if (data.signaturePic) {
      const signaturePic = new UserSignaturePic();
      signaturePic.hydrate(data.signaturePic);

      this.signaturePic = signaturePic;
    }

    if (data.companyLogo) {
      const companyLogo = new UserCompanyLogo();
      companyLogo.hydrate(data.companyLogo);

      this.companyLogo = companyLogo;
    }

    if (data.calendar) {
      const calendar = new UserCalendarProvider();
      calendar.hydrate(data.calendar);

      this.calendar = calendar;
    }
  }

  public getUserId() {
    return this.userId;
  }

  public getUserName() {
    return ((this.getUserFirstname() || '') + ' ' + (this.getUserLastname() || '')).trim();
  }

  public getUserStatus() {
    return this.userStatus;
  }

  public getUserTrialStart() {
    return this.userTrialStart;
  }

  public getUserUsername() {
    return this.userUsername;
  }

  public getUserCompanyName() {
    return this.userCompanyName;
  }

  public getUserCompanyLogo(): UserCompanyLogo {
    return this.companyLogo;
  }

  public getUserFirstname() {
    return this.userFirstname;
  }

  public getUserLastname() {
    return this.userLastname;
  }

  public getUserAddressStreet() {
    return this.userAddressStreet;
  }

  public getUserAddressUnit() {
    return this.userAddressUnit;
  }

  public getUserAddressCity() {
    return this.userAddressCity;
  }

  public getUserAddressState() {
    return this.userAddressState;
  }

  public getUserAddressZip() {
    return this.userAddressZip;
  }

  public getUserPhone() {
    return this.userPhone;
  }

  public getUserHearabout() {
    return this.userHearabout;
  }

  public getUserFmo() {
    return this.userFmo;
  }

  public getUserCurrentDb() {
    return this.userCurrentDb;
  }

  public getUserAgree() {
    return this.userAgree;
  }

  public getUserTimezone() {
    return this.userTimezone;
  }

  public getUserMfa() {
    return this.userMfa;
  }

  public getUserMfaNumber() {
    return this.userMfaNumber;
  }

  public getUserPhoneProvider() {
    return this.userPhoneProvider;
  }

  public getUserLastLogin() {
    return this.userLastLogin;
  }

  public getUserTitle() {
    return this.userTitle;
  }

  public getNpn() {
    return this.userNpn;
  }

  public getProfilePic(): UserProfilePic {
    return this.userProfilePic;
  }

  public getDocuments(type: string = null) {
    if (type) {
      const filteredDocuments = this.documents
        .getItems()
        .filter((document) => document.getDocType() === type);
      const documents = new UserDocumentCollection();

      filteredDocuments.forEach((singleDocument) => {
        documents.addItem(singleDocument);
      });

      return filteredDocuments;
    } else {
      return this.documents;
    }
  }

  public getIps() {
    return this.ips;
  }

  public getLetters() {
    return this.letters;
  }

  public getClientPortals() {
    return this.clientPortals;
  }

  public getOffices() {
    return this.offices;
  }

  public getNotificationPreferences() {
    return this.notificationPreferences;
  }

  public getCalendar() {
    return this.calendar;
  }

  public getSignature() {
    return this.signature;
  }

  public getSignatureEmail() {
    return this.signatureEmail;
  }

  public getSignaturePic() {
    return this.signaturePic;
  }

  public getUserSunfire() {
    return this.userSunfire;
  }

  public getUserConnecture() {
    return this.userConnecture;
  }

  public getUserPersonalTokens() {
    return this.userPersonalTokens;
  }

  public getShowCalendarOnDashboard() {
    return this.showCalendarOnDashboard;
  }

  public getAuthorName(): string {
    const first = this.userFirstname;
    const lastInitial = this.userLastname?.charAt(0).toUpperCase() ?? '';
    return `${first} ${lastInitial}`;
  }
}
